import { Button } from "@client/components/Button";
import { formatClock } from "@client/lib/datetime";
import { describeError } from "@client/lib/errorMessage";
import type { ScenarioSave } from "@client/hooks/useScenarioSave";

/**
 * What the save pipeline is doing, in the header, next to the Save button.
 *
 * An editor that saves on its own has to say so continuously or people save by
 * habit anyway and never trust it. The four states are the ones a person can act
 * on: it is safe to close, it is happening, it has not happened yet, it failed.
 * `aria-live="polite"` means a screen reader hears the transitions without being
 * interrupted mid-sentence.
 */
export function SaveStatus({ save, canEdit }: { save: ScenarioSave; canEdit: boolean }) {
  if (!canEdit) {
    return (
      <span className="text-sm text-ink-soft" role="status">
        Read-only
      </span>
    );
  }

  // Offline outranks every status but "saving": the reason nothing is being
  // saved is the connection, and repeating "unsaved changes" while the banner
  // overhead says why is one message too few and two words too many.
  const label = save.offline
    ? save.status === "dirty" || save.status === "error"
      ? "Offline — changes kept here"
      : "Offline"
    : save.status === "saving"
      ? "Saving…"
      : save.status === "error"
        ? "Save failed"
        : save.status === "dirty"
          ? "Unsaved changes"
          : save.lastSavedAt
            ? `Saved ${formatClock(save.lastSavedAt)}`
            : "Saved";

  const color = save.offline
    ? "var(--chart-warn)"
    : save.status === "error"
      ? "var(--chart-crit)"
      : save.status === "clean"
        ? "var(--chart-ok)"
        : undefined;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span
        role="status"
        aria-live="polite"
        className="text-sm font-semibold"
        style={color ? { color } : { color: "var(--ink-soft)" }}
      >
        {label}
      </span>

      {save.status === "error" && !save.offline && (
        <span className="max-w-[18rem] text-xs text-ink-soft">{describeError(save.error)}</span>
      )}

      <Button
        size="sm"
        variant={save.status === "error" && !save.offline ? "primary" : "ghost"}
        onClick={save.saveNow}
        // Disabled rather than hidden while offline: the button is where people
        // look for the state of their work, and a missing one reads as a bug.
        disabled={save.status === "saving" || save.offline}
        // The shortcut is discoverable here or nowhere; nobody reads a help page
        // to find out an editor supports Ctrl+S.
        title={save.offline ? "Saves automatically when the connection returns" : "Ctrl+S"}
      >
        {save.status === "error" && !save.offline ? "Retry save" : "Save"}
      </Button>
    </div>
  );
}
